import { Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { REQUEST } from 'zenith/context';
import { Request } from 'express';
export interface CookieOptions {
  expires?: Date | number;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CookieService {
  private readonly isBrowser: boolean;
  private readonly request: Request;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: string,
    private readonly injector: Injector
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.request = this.injector.get(REQUEST, null);
  }

  /**
   * Set a cookie with the given name, value, and options
   * @param name - Cookie name
   * @param value - Cookie value
   * @param options - Cookie options (expires, path, domain, secure, sameSite)
   */
  public set(name: string, value: string, options: CookieOptions = {}): void {
    if (!this.isBrowser) {
      console.warn('CookieService: Cannot set cookies on server-side');
      return;
    }

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (options.expires) {
      const expires =
        options.expires instanceof Date
          ? options.expires
          : new Date(Date.now() + options.expires * 24 * 60 * 60 * 1000);
      cookieString += `; expires=${expires.toUTCString()}`;
    }

    if (options.path) {
      cookieString += `; path=${options.path}`;
    }

    if (options.domain) {
      cookieString += `; domain=${options.domain}`;
    }

    if (options.secure) {
      cookieString += '; secure';
    }

    if (options.sameSite) {
      cookieString += `; samesite=${options.sameSite}`;
    }

    document.cookie = cookieString;
  }

  /**
   * Get a cookie value by name
   * @param name - Cookie name
   * @returns Cookie value or null if not found
   */
  public get(name: string): string | null {
    if (!this.isBrowser) {
      return this.request?.cookies?.[name] || null;
    }

    return this.getCookieFromStr(document.cookie, name);
  }

  /**
   * Check if a cookie exists
   * @param name - Cookie name
   * @returns True if cookie exists, false otherwise
   */
  public has(name: string): boolean {
    return this.get(name) !== null;
  }

  /**
   * Remove a cookie by setting its expiration date to the past
   * @param name - Cookie name
   * @param options - Cookie options (path, domain)
   */
  public remove(name: string, options: Pick<CookieOptions, 'path' | 'domain'> = {}): void {
    if (!this.isBrowser) {
      console.warn('CookieService: Cannot remove cookies on server-side');
      return;
    }

    this.set(name, '', {
      ...options,
      expires: new Date(0),
    });
  }

  /**
   * Get cookie value as JSON (parses the value)
   * @param name - Cookie name
   * @returns Parsed JSON value or null if not found/invalid
   */
  public getAsJson<T = unknown>(name: string): T | null {
    const value = this.get(name);
    if (!value) {
      return null;
    }

    try {
      return JSON.parse(value) as T;
    } catch (error) {
      console.warn(`CookieService: Failed to parse JSON for cookie '${name}'`, error);
      return null;
    }
  }

  /**
   * Set cookie value as JSON (stringifies the value)
   * @param name - Cookie name
   * @param value - Value to be stringified
   * @param options - Cookie options
   */
  public setAsJson(name: string, value: unknown, options: CookieOptions = {}): void {
    const jsonValue = JSON.stringify(value);
    this.set(name, jsonValue, options);
  }

  private getCookieFromStr(cookies: string, name: string): string | null {
    const cookieArray = cookies.split(';').map(cookie => cookie.trim());

    for (const cookie of cookieArray) {
      const equalIndex = cookie.indexOf('=');

      if (equalIndex < 0) {
        return null;
      }

      const currentName = decodeURIComponent(cookie.substring(0, equalIndex));
      if (name === currentName) {
        return decodeURIComponent(cookie.substring(equalIndex + 1));
      }
    }

    return null;
  }
}
